"use client"

import { useRef, useState, type ReactNode } from "react"
import { Camera, FileUp } from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export type FileDropzoneProps = {
  onFiles: (files: File[]) => void
  accept?: string
  multiple?: boolean
  disabled?: boolean
  /** Компактный вариант для сайдбара / «добавить ещё» */
  compact?: boolean
  /** Горизонтальная полоса: кнопка и подпись в одну линию, сброс файлов на всю область */
  inline?: boolean
  title?: string
  hint?: string
  /** Короткая подпись вместо hint на экранах уже lg. Десктопная полоса не меняется. */
  mobileHint?: string
  buttonLabel?: string
  className?: string
  /** Принудительно показать состояние drag (для витрины стилей) */
  forceDragOver?: boolean
  /** Под кнопкой выбора, клик не открывает диалог файлов */
  footer?: ReactNode
  /**
   * Кнопка «Сфотографировать» (capture=environment) — на мобильных открывает камеру,
   * обычно отдаёт JPEG без HEIC. Показывается только на узких экранах (max-sm).
   */
  showCameraButton?: boolean
  cameraButtonLabel?: string
}

/**
 * Зона загрузки файлов (дизайн-система портала).
 * Стиль: info — синяя пунктирная рамка, кнопка variant="info".
 */
export function FileDropzone({
  onFiles,
  accept,
  multiple = true,
  disabled,
  compact,
  inline = false,
  title,
  hint = "PDF, PNG, JPEG, WEBP",
  mobileHint,
  buttonLabel = "Выбрать файлы",
  className,
  forceDragOver,
  footer,
  showCameraButton = false,
  cameraButtonLabel = "Сфотографировать",
}: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const cameraRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const active = forceDragOver || dragOver

  function pick(list: FileList | File[] | null) {
    if (!list) return
    const files = Array.from(list)
    if (files.length) onFiles(files)
  }

  const heading =
    title ??
    (compact ? "Добавить ещё файлы" : "Перетащите файлы сюда или выберите на диске")

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      onKeyDown={(e) => {
        if (disabled) return
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragOver(false)
        if (!disabled) pick(e.dataTransfer.files)
      }}
      onClick={() => {
        if (!disabled) inputRef.current?.click()
      }}
      className={cn(
        "flex w-full cursor-pointer items-center rounded-xl border-2 border-dashed text-center transition-colors outline-none",
        inline ? "min-w-0 flex-col items-stretch justify-start" : "flex-col justify-center",
        "border-info/45 bg-info/5",
        "hover:border-info hover:bg-info/10",
        "focus-visible:border-info focus-visible:ring-3 focus-visible:ring-info/30",
        inline
          ? "gap-3 px-4 py-3 text-left lg:flex-row lg:flex-nowrap lg:items-center lg:justify-between lg:gap-x-4 lg:gap-y-3"
          : compact
            ? "gap-2 px-4 py-5"
            : "gap-3 px-6 py-12 sm:py-14",
        active && "border-info bg-info/15 ring-3 ring-info/25",
        disabled && "pointer-events-none cursor-not-allowed opacity-50",
        className,
      )}
    >
      <div
        className={cn(
          "flex min-w-0 items-center",
          inline ? "w-full gap-3 lg:w-auto lg:flex-1" : "flex-col gap-3",
        )}
      >
        <div
          className={cn(
            "flex shrink-0 items-center justify-center rounded-full bg-info/15 text-info",
            inline || compact ? "size-9" : "size-12",
          )}
        >
          <FileUp className={cn("shrink-0", inline || compact ? "h-4 w-4" : "h-5 w-5")} />
        </div>

        <div className={cn("min-w-0 space-y-1", inline && "text-left")}>
          <p
            className={cn(
              "font-semibold text-foreground",
              inline || compact ? "text-sm" : "text-base",
            )}
          >
            {heading}
          </p>
          <p
            className={cn(
              "text-muted-foreground",
              inline || compact ? "text-xs" : "text-sm",
              mobileHint && "hidden lg:block",
            )}
          >
            {hint}
          </p>
          {mobileHint ? (
            <p
              className={cn(
                "text-muted-foreground lg:hidden",
                inline || compact ? "text-xs" : "text-sm",
              )}
            >
              {mobileHint}
            </p>
          ) : null}
          {!inline && !compact && multiple ? (
            <p className="text-xs text-muted-foreground">
              Можно перетащить несколько файлов сразу
            </p>
          ) : null}
        </div>
      </div>

      <div
        className={cn(
          "flex items-center gap-2",
          inline
            ? "w-full flex-col items-stretch lg:w-auto lg:flex-row lg:flex-wrap lg:items-center lg:justify-end lg:shrink-0"
            : "flex-wrap justify-center",
        )}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Button
          type="button"
          variant="info"
          size={inline || compact ? "sm" : "default"}
          disabled={disabled}
          className={cn("gap-1.5 whitespace-nowrap", inline && "w-full lg:w-auto")}
          onClick={(e) => {
            e.stopPropagation()
            inputRef.current?.click()
          }}
        >
          <FileUp className="h-4 w-4 shrink-0" />
          {buttonLabel}
        </Button>

        {showCameraButton ? (
          <div className={cn("sm:hidden", inline && "w-full")}>
            <Button
              type="button"
              variant="secondary"
              size={inline || compact ? "sm" : "default"}
              disabled={disabled}
              className={cn("gap-1.5 whitespace-nowrap", inline && "w-full")}
              onClick={(e) => {
                e.stopPropagation()
                cameraRef.current?.click()
              }}
            >
              <Camera className="h-4 w-4 shrink-0" />
              {cameraButtonLabel}
            </Button>
          </div>
        ) : null}

        {inline && footer ? (
          <div
            className="flex w-full justify-start lg:w-auto lg:justify-end"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {footer}
          </div>
        ) : null}
      </div>

      {!inline && footer ? (
        <div
          className="flex justify-center"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {footer}
        </div>
      ) : null}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => {
          pick(e.target.files)
          e.target.value = ""
        }}
      />

      {showCameraButton ? (
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          tabIndex={-1}
          onChange={(e) => {
            pick(e.target.files)
            e.target.value = ""
          }}
        />
      ) : null}
    </div>
  )
}
